/**
 * Advice Bot API Client
 *
 * Frontend service to communicate with advice-bot-backend API
 * Provides sentiment analysis and trading signals
 */

import { logger } from './logger';
import {
  ApiResponseSchema,
  TwitterSentimentSchema,
  RedditSentimentSchema,
  NewsSentimentSchema,
  AggregatedSentimentSchema,
  TechnicalIndicatorsSchema,
  TradingSignalSchema,
  BacktestHistoricalDataSchema,
  BacktestResultSchema,
  SignalExplanationSchema,
  SentimentHistoryResponseSchema,
  validateSchema
} from '../schemas/api.schemas';
import { z } from 'zod';

// Backend API URL (configured via environment variable)
// Vercel Serverless: Uses /api/advice endpoints (same domain)
const API_BASE_URL = import.meta.env.VITE_ADVICE_BOT_API_URL || '';

// Types
export interface TwitterSentiment {
  symbol: string;
  mentions: number;
  sentiment: number; // -100 to +100
  influencerMentions: number;
  followers: number;
  trend: 'up' | 'down' | 'stable';
  timestamp: number;
}

export interface RedditSentiment {
  symbol: string;
  mentions: number;
  sentiment: number; // -100 to +100
  upvoteRatio: number;
  trend: 'up' | 'down' | 'stable';
  subreddits: string[];
  timestamp: number;
}

export interface NewsSentiment {
  symbol: string;
  mentions: number;
  sentiment: number; // -100 to +100
  sources: string[];
  trend: 'up' | 'down' | 'stable';
  topHeadlines: Array<{
    title: string;
    url: string;
    source: string;
    sentiment: 'positive' | 'negative' | 'neutral';
  }>;
  timestamp: number;
}

export interface AggregatedSentiment {
  symbol: string;
  twitter: TwitterSentiment;
  reddit: RedditSentiment;
  news: NewsSentiment;
  overall: {
    score: number; // -100 to +100
    sentiment: 'bullish' | 'neutral' | 'bearish';
    confidence: number; // 0-100
  };
  timestamp: number;
}

export interface TechnicalIndicators {
  symbol: string;
  timeframe: string;
  rsi: number;
  macd: {
    value: number;
    signal: number;
    histogram: number;
  };
  bollingerBands: {
    upper: number;
    middle: number;
    lower: number;
  };
  movingAverages: {
    sma20: number;
    sma50: number;
    sma200: number;
    ema12: number;
    ema26: number;
  };
  volume: {
    current: number;
    average24h: number;
    ratio: number;
  };
  timestamp: number;
}

export interface TradingSignal {
  tokenId: string;
  tokenName: string;
  action: 'buy' | 'sell' | 'hold';
  confidence: number; // 0-100
  reason: string;
  currentPrice: number;
  suggestedPrice?: number;
  timeframe: string;
  sources?: {
    technical?: any;
    sentiment?: AggregatedSentiment;
  };
  timestamp: number;
}

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
  cached?: boolean;
  timestamp: number;
}

export interface FearGreedIndex {
  value: number; // 0-100
  classification: 'Extreme Fear' | 'Fear' | 'Neutral' | 'Greed' | 'Extreme Greed';
  timestamp: number;
}

/**
 * Advice Bot API Client
 */
export class AdviceBotAPI {
  /**
   * Check if backend is available
   */
  static async healthCheck(): Promise<boolean> {
    try {
      // Test with Fear & Greed endpoint (doesn't require API keys)
      const response = await fetch(`${API_BASE_URL}/api/advice/market?type=fear-greed`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      return response.ok;
    } catch (error) {
      logger.error('AdviceBotAPI', 'Health check failed', error);
      return false;
    }
  }

  /**
   * Get Fear & Greed Index from Alternative.me
   */
  static async getFearGreedIndex(): Promise<FearGreedIndex | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/market/fear-greed`);

      if (!response.ok) {
        logger.warn('AdviceBotAPI', 'Fear & Greed API not available');
        return null;
      }

      const data = await response.json();

      if (data.success && data.data) {
        return data.data;
      }

      logger.error('AdviceBotAPI', 'Fear & Greed error', data.error);
      return null;
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to fetch Fear & Greed Index', error);
      return null;
    }
  }

  /**
   * Get Twitter/X sentiment for a symbol
   */
  static async getTwitterSentiment(symbol: string): Promise<TwitterSentiment | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advice/sentiment?type=twitter&symbol=${symbol}`);
      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(TwitterSentimentSchema),
        rawData,
        `getTwitterSentiment(${symbol})`
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid Twitter sentiment response', { symbol, rawData });
        return null;
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'Twitter sentiment error', validatedResponse.error);
      return null;
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to fetch Twitter sentiment', error);
      return null;
    }
  }

  /**
   * Get Reddit sentiment for a symbol
   */
  static async getRedditSentiment(symbol: string): Promise<RedditSentiment | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advice/sentiment?type=reddit&symbol=${symbol}`);
      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(RedditSentimentSchema),
        rawData,
        `getRedditSentiment(${symbol})`
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid Reddit sentiment response', { symbol, rawData });
        return null;
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'Reddit sentiment error', validatedResponse.error);
      return null;
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to fetch Reddit sentiment', error);
      return null;
    }
  }

  /**
   * Get news sentiment for a symbol
   */
  static async getNewsSentiment(symbol: string): Promise<NewsSentiment | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advice/sentiment?type=news&symbol=${symbol}`);
      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(NewsSentimentSchema),
        rawData,
        `getNewsSentiment(${symbol})`
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid news sentiment response', { symbol, rawData });
        return null;
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'News sentiment error', validatedResponse.error);
      return null;
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to fetch news sentiment', error);
      return null;
    }
  }

  /**
   * Get aggregated sentiment from all sources
   */
  static async getAggregatedSentiment(symbol: string): Promise<AggregatedSentiment | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advice/sentiment?type=aggregated&symbol=${symbol}`);
      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(AggregatedSentimentSchema),
        rawData,
        `getAggregatedSentiment(${symbol})`
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid aggregated sentiment response', { symbol, rawData });
        return null;
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'Aggregated sentiment error', validatedResponse.error);
      return null;
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to fetch aggregated sentiment', error);
      return null;
    }
  }

  /**
   * Get technical indicators for a symbol
   */
  static async getTechnicalIndicators(
    symbol: string,
    timeframe: string = '1d'
  ): Promise<TechnicalIndicators | null> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/advice/technical?type=indicators&symbol=${symbol}&timeframe=${timeframe}`
      );
      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(TechnicalIndicatorsSchema),
        rawData,
        `getTechnicalIndicators(${symbol}, ${timeframe})`
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid technical indicators response', { symbol, timeframe, rawData });
        return null;
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'Technical indicators error', validatedResponse.error);
      return null;
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to fetch technical indicators', error);
      return null;
    }
  }

  /**
   * Get trading signal for a single symbol
   */
  static async getTradingSignal(
    symbol: string,
    currentPrice: number,
    options: {
      enableSentiment?: boolean;
      enableTechnical?: boolean;
      riskTolerance?: number;
    } = {}
  ): Promise<TradingSignal | null> {
    try {
      const {
        enableSentiment = true,
        enableTechnical = true,
        riskTolerance = 5
      } = options;

      const params = new URLSearchParams({
        price: currentPrice.toString(),
        enableSentiment: enableSentiment.toString(),
        enableTechnical: enableTechnical.toString(),
        riskTolerance: riskTolerance.toString()
      });

      const response = await fetch(
        `${API_BASE_URL}/api/advice/trading?type=signal&symbol=${symbol}&${params}`
      );
      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(TradingSignalSchema),
        rawData,
        `getTradingSignal(${symbol})`
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid trading signal response', { symbol, rawData });
        return null;
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'Trading signal error', validatedResponse.error);
      return null;
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to fetch trading signal', error);
      return null;
    }
  }

  /**
   * Get trading signals for multiple symbols
   */
  static async getTradingSignals(
    symbolsWithPrices: Array<{ symbol: string; price: number }>,
    options: {
      enableSentiment?: boolean;
      enableTechnical?: boolean;
      riskTolerance?: number;
    } = {}
  ): Promise<TradingSignal[]> {
    try {
      const {
        enableSentiment = true,
        enableTechnical = true,
        riskTolerance = 5
      } = options;

      const symbols = symbolsWithPrices.map(s => s.symbol).join(',');
      const prices = symbolsWithPrices.reduce((acc, s) => {
        acc[s.symbol] = s.price;
        return acc;
      }, {} as Record<string, number>);

      const params = new URLSearchParams({
        symbols,
        prices: JSON.stringify(prices),
        enableSentiment: enableSentiment.toString(),
        enableTechnical: enableTechnical.toString(),
        riskTolerance: riskTolerance.toString()
      });

      const response = await fetch(`${API_BASE_URL}/api/advice/trading?type=signals&${params}`);
      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(z.array(TradingSignalSchema)),
        rawData,
        'getTradingSignals'
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid trading signals response', { rawData });
        return [];
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'Trading signals error', validatedResponse.error);
      return [];
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to fetch trading signals', error);
      return [];
    }
  }

  /**
   * Convert sentiment data to UI format
   * Maps API response to Bot.tsx SentimentData interface
   */
  static mapToSentimentData(aggregated: AggregatedSentiment): {
    source: string;
    sentiment: 'bullish' | 'neutral' | 'bearish';
    score: number;
    mentions: number;
    trend: 'up' | 'down' | 'stable';
  }[] {
    const getSentimentCategory = (score: number): 'bullish' | 'neutral' | 'bearish' => {
      if (score > 20) return 'bullish';
      if (score < -20) return 'bearish';
      return 'neutral';
    };

    return [
      {
        source: 'Twitter/X',
        sentiment: getSentimentCategory(aggregated.twitter.sentiment),
        score: aggregated.twitter.sentiment,
        mentions: aggregated.twitter.mentions,
        trend: aggregated.twitter.trend
      },
      {
        source: 'Reddit',
        sentiment: getSentimentCategory(aggregated.reddit.sentiment),
        score: aggregated.reddit.sentiment,
        mentions: aggregated.reddit.mentions,
        trend: aggregated.reddit.trend
      },
      {
        source: 'News Media',
        sentiment: getSentimentCategory(aggregated.news.sentiment),
        score: aggregated.news.sentiment,
        mentions: aggregated.news.mentions,
        trend: aggregated.news.trend
      }
    ];
  }

  // ============================================
  // BACKTESTING APIs (Phase 3)
  // ============================================

  /**
   * Get historical price data for a token
   */
  static async getHistoricalData(
    coinId: string,
    days: number = 365
  ): Promise<BacktestHistoricalData | null> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/advice/backtesting?type=historical&coinId=${coinId}&days=${days}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(BacktestHistoricalDataSchema),
        rawData,
        `getHistoricalData(${coinId})`
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid historical data response', { coinId, rawData });
        return null;
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'Historical data error', validatedResponse.error);
      return null;
    } catch (error) {
      logger.error('AdviceBotAPI', `Failed to fetch historical data for ${coinId}`, error);
      return null;
    }
  }

  /**
   * Run backtest for a single token
   */
  static async runBacktest(
    symbol: string,
    coinId: string,
    config: BacktestConfig,
    days: number = 365
  ): Promise<BacktestResult | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advice/backtesting?type=run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          symbol,
          coinId,
          days,
          config
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(BacktestResultSchema),
        rawData,
        `runBacktest(${symbol})`
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid backtest response', { symbol, rawData });
        return null;
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'Backtest error', validatedResponse.error);
      return null;
    } catch (error) {
      logger.error('AdviceBotAPI', `Failed to run backtest for ${symbol}`, error);
      return null;
    }
  }

  /**
   * Run backtests for multiple tokens
   */
  static async runBatchBacktest(
    tokens: Array<{ symbol: string; coinId: string }>,
    config: BacktestConfig,
    days: number = 365
  ): Promise<BacktestResult[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advice/backtesting?type=run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tokens,
          days,
          config
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(z.array(BacktestResultSchema)),
        rawData,
        'runBatchBacktest'
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid batch backtest response', { rawData });
        return [];
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'Batch backtest error', validatedResponse.error);
      return [];
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to run batch backtest', error);
      return [];
    }
  }

  /**
   * Compare strategies for multiple tokens
   */
  static async compareStrategies(
    coinIds: string[],
    days: number = 365
  ): Promise<any[]> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/advice/backtesting?type=compare&coinIds=${coinIds.join(',')}&days=${days}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        return data.data;
      }

      logger.error('AdviceBotAPI', 'Strategy comparison error', data.error);
      return [];
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to compare strategies', error);
      return [];
    }
  }

  // ============================================
  // AI EXPLAINER APIs (Phase 3.2)
  // ============================================

  /**
   * Get AI-powered explanation for a trading signal
   */
  static async explainSignal(
    signal: TradingSignal,
    sentiment?: AggregatedSentiment,
    technical?: TechnicalIndicators
  ): Promise<SignalExplanation | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advice/explainer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          signal,
          sentiment,
          technical
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(SignalExplanationSchema),
        rawData,
        `explainSignal(${signal.tokenName})`
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid signal explanation response', { signal: signal.tokenName, rawData });
        return null;
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'Signal explanation error', validatedResponse.error);
      return null;
    } catch (error) {
      logger.error('AdviceBotAPI', `Failed to explain signal for ${signal.tokenName}`, error);
      return null;
    }
  }

  // ============================================
  // NOTIFICATION APIs (Phase 3.4)
  // ============================================

  /**
   * Send test notification
   */
  static async sendTestNotification(
    channel: 'email' | 'discord' | 'telegram',
    settings: NotificationSettings
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advice/notifications?type=test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          channel,
          settings
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        return { success: true };
      }

      return { success: false, error: data.error?.message || 'Test failed' };
    } catch (error: any) {
      logger.error('AdviceBotAPI', `Failed to send test ${channel} notification`, error);
      return { success: false, error: error.message || 'Network error' };
    }
  }

  /**
   * Send signal notification
   */
  static async notifySignal(
    settings: NotificationSettings,
    signal: TradingSignal
  ): Promise<{ success: boolean; sent: number; failed: number }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advice/notifications?type=signal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          settings,
          signal
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        return {
          success: true,
          sent: data.data.sent || 0,
          failed: data.data.failed || 0
        };
      }

      return { success: false, sent: 0, failed: 0 };
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to send signal notification', error);
      return { success: false, sent: 0, failed: 0 };
    }
  }

  /**
   * Send backtest complete notification
   */
  static async notifyBacktestComplete(
    settings: NotificationSettings,
    symbol: string,
    winRate: number,
    totalReturn: number
  ): Promise<{ success: boolean; sent: number; failed: number }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advice/notifications?type=backtest-complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          settings,
          symbol,
          winRate,
          totalReturn
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        return {
          success: true,
          sent: data.data.sent || 0,
          failed: data.data.failed || 0
        };
      }

      return { success: false, sent: 0, failed: 0 };
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to send backtest complete notification', error);
      return { success: false, sent: 0, failed: 0 };
    }
  }

  /**
   * Get historical sentiment data for a symbol (Phase 4.4)
   * @param symbol - Cryptocurrency symbol (e.g., 'BTC', 'ETH')
   * @param days - Number of days of history (default 365, max 365)
   */
  static async getHistoricalSentiment(
    symbol: string,
    days: number = 365
  ): Promise<SentimentHistoryResponse | null> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/advice/sentiment?type=historical&symbol=${symbol}&days=${Math.min(days, 365)}`
      );

      if (!response.ok) {
        logger.warn('AdviceBotAPI', `Failed to fetch sentiment history for ${symbol}`, response.statusText);
        return null;
      }

      const rawData = await response.json();

      // Validate response structure
      const validatedResponse = validateSchema(
        ApiResponseSchema(SentimentHistoryResponseSchema),
        rawData,
        `getHistoricalSentiment(${symbol})`
      );

      if (!validatedResponse) {
        logger.error('AdviceBotAPI', 'Invalid sentiment history response', { symbol, rawData });
        return null;
      }

      if (validatedResponse.success && validatedResponse.data) {
        return validatedResponse.data;
      }

      logger.error('AdviceBotAPI', 'Sentiment history error', validatedResponse.error);
      return null;
    } catch (error) {
      logger.error('AdviceBotAPI', 'Failed to fetch sentiment history', error);
      return null;
    }
  }
}

// ============================================
// BACKTESTING TYPES (Phase 3)
// ============================================

export interface BacktestHistoricalData {
  symbol: string;
  coinId: string;
  prices: Array<{
    timestamp: number;
    price: number;
    marketCap?: number;
    volume?: number;
  }>;
  startDate: number;
  endDate: number;
  dataPoints: number;
  statistics: {
    min: number;
    max: number;
    avg: number;
    median: number;
    volatility: number;
    volatilityPercent: number;
    totalReturn: number;
    startPrice: number;
    endPrice: number;
    dataPoints: number;
  };
}

export interface BacktestTrade {
  timestamp: number;
  action: 'buy' | 'sell';
  symbol: string;
  price: number;
  quantity: number;
  positionValue: number;
  confidence: number;
  reason: string;
}

export interface BacktestResult {
  symbol: string;
  startDate: number;
  endDate: number;
  initialCapital: number;
  finalCapital: number;
  totalReturn: number;
  totalReturnPercent: number;
  trades: BacktestTrade[];
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  avgWinPercent: number;
  avgLossPercent: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  sharpeRatio: number;
  profitFactor: number;
  benchmarkReturn: number;
  excessReturn: number;
}

export interface BacktestConfig {
  initialCapital: number;
  maxPositionSize: number; // 0-1 (e.g., 0.3 = 30%)
  stopLossPercent: number;
  takeProfitPercent: number;
  riskTolerance: number; // 1-10
  enableSentiment: boolean;
  enableTechnical: boolean;
  commissionPercent: number;
}

// ============================================
// AI EXPLAINER TYPES (Phase 3.2)
// ============================================

export interface SignalExplanation {
  signal: TradingSignal;
  explanation: string;
  keyFactors: string[];
  risks: string[];
  confidence: number;
  educationalNote: string;
  timestamp: number;
}

// ============================================
// SENTIMENT HISTORY TYPES (Phase 4.4)
// ============================================

export interface DailySentiment {
  date: string; // ISO date string (YYYY-MM-DD)
  sentiment: number; // -100 (very bearish) to +100 (very bullish)
  mentions: number; // Total mentions across all sources
  sources: {
    twitter: number; // Sentiment score from Twitter
    reddit: number; // Sentiment score from Reddit
    news: number; // Sentiment score from News
  };
}

export interface SentimentHistoryResponse {
  symbol: string;
  days: number;
  history: DailySentiment[];
  stats: {
    bullishDays: number;
    bearishDays: number;
    neutralDays: number;
    avgSentiment: number;
    maxSentiment: number;
    minSentiment: number;
    totalDays: number;
  };
}

// ============================================
// NOTIFICATION TYPES (Phase 3.4)
// ============================================

export interface NotificationSettings {
  email?: string;
  discord?: string; // Webhook URL
  telegram?: string; // Chat ID
  triggers: {
    highConfidenceSignals: boolean; // >80%
    portfolioAlerts: boolean; // -10% drop
    backtestComplete: boolean;
  };
}
